import React, { useState, useEffect } from "react";
import { Alert, AlertTitle, Box, Button, List, ListItem, ListItemText } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { restFetch } from "../../../../api/apiClient";
import { useRootStore } from "../../../../store/contexts";

/**
 * Cartel de configuración inicial del tenant. Se muestra hasta que el perfil
 * ("Datos del Cliente") tenga TODOS los datos que el circuito necesita para operar
 * (crear ediciones → documentación → firma → SADAIC → DNDA); en cuanto está
 * completo, no se muestra nada. Reutilizable en Inicio, Datos del Cliente (/list) y
 * Editoriales (/list). Autónomo: consulta el estado por una query liviana al
 * montar (mismo patrón que FirstPublisherBanner).
 *
 * Requiere permiso de lectura de TenantProfile (SiteAdmin). Para roles sin ese
 * permiso la query vuelve vacía y el cartel no se muestra.
 */

type Missing =
  | "ownPublisher"
  | "signer"
  | "sadaicFtp";

export const TenantSetupGuide: React.FC = () => {
  const { langStore } = useRootStore();
  const navigate = useNavigate();
  const t = langStore as any;
  const [missing, setMissing] = useState<Missing[] | null>(null);
  const [profileId, setProfileId] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await restFetch("/graphql", {
          method: "POST",
          body: JSON.stringify({
            query: `query TenantSetupStatus {
              tenantProfileListWithOffset(offset: 0, take: 1) {
                id
                sadaicFtpUrl
                sadaicFtpUser
                sadaicFtpPasswordHasValue
                ownPublisher {
                  id
                  representatives { isSigner email }
                }
              }
            }`,
          }),
        });
        const p = data?.data?.tenantProfileListWithOffset?.[0];
        if (!p) {
          if (alive) setMissing(null); // sin perfil legible (permiso/rol) → no molestar
          return;
        }
        if (alive) setProfileId(p.id ?? null);
        const nonEmpty = (v: unknown) =>
          typeof v === "string" ? v.trim().length > 0 : v != null;
        const list: Missing[] = [];
        if (!p.ownPublisher?.id) {
          list.push("ownPublisher");
        } else {
          const hasSigner = (p.ownPublisher.representatives ?? []).some(
            (r: any) => r?.isSigner === true && nonEmpty(r?.email)
          );
          if (!hasSigner) list.push("signer");
        }
        if (!nonEmpty(p.sadaicFtpUrl) || !nonEmpty(p.sadaicFtpUser) || !p.sadaicFtpPasswordHasValue) {
          list.push("sadaicFtp");
        }
        if (alive) setMissing(list);
      } catch {
        if (alive) setMissing(null);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (!missing || missing.length === 0) return null;

  const itemText: Record<Missing, string> = {
    ownPublisher: t.tenantSetupItemOwnPublisher,
    signer: t.tenantSetupItemSigner,
    sadaicFtp: t.tenantSetupItemFtp,
  };
  const needsPublisher = missing.includes("ownPublisher");

  return (
    <Alert severity="warning" sx={{ mb: 2 }}>
      <AlertTitle>{t.tenantSetupTitle}</AlertTitle>
      {t.tenantSetupIntro}
      <List dense sx={{ py: 0.5 }}>
        {missing.map((m) => (
          <ListItem key={m} sx={{ py: 0 }}>
            <ListItemText primary={`• ${itemText[m]}`} />
          </ListItem>
        ))}
      </List>
      <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
        {needsPublisher ? (
          // Aún sin editorial → crear la primera (se auto-asigna como propia).
          <Button size="small" variant="contained" onClick={() => navigate("/publisher/create")}>
            {t.tenantSetupCreatePublisher}
          </Button>
        ) : (
          // Ya hay editorial, falta completar el perfil → editar el TenantProfile.
          <Button
            size="small"
            variant="contained"
            disabled={!profileId}
            onClick={() => profileId && navigate(`/tenantProfile/update/${profileId}`)}
          >
            {t.tenantSetupEditProfile}
          </Button>
        )}
      </Box>
    </Alert>
  );
};
